import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Edit, Trash2, Shield, Users, Lock } from 'lucide-react';

const RolesList = ({ systemRoles, customRoles, onEdit, onDelete, permissions }) => {
  
  const RoleCard = ({ role, isSystem = false }) => {
    const userCountColor = role.users_count > 0 ? 'text-emerald-600' : 'text-gray-400';
    
    return (
      <Card className="hover:shadow-md transition-shadow">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              {isSystem ? (
                <Lock className="h-5 w-5 text-blue-600" />
              ) : (
                <Shield className="h-5 w-5 text-purple-600" />
              )}
              <div>
                <CardTitle className="text-lg">{role.name}</CardTitle>
                <CardDescription className="text-sm mt-1">
                  {role.description}
                </CardDescription>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {isSystem && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  Système
                </Badge>
              )}
              {!isSystem && role.is_active && (
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                  Actif
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Users className={`h-4 w-4 ${userCountColor}`} />
                <span className={userCountColor}>
                  {role.users_count} utilisateur{role.users_count > 1 ? 's' : ''}
                </span>
              </div>
              
              {!isSystem && role.permissions && (
                <div className="text-gray-600">
                  {role.permissions.length} permission{role.permissions.length > 1 ? 's' : ''}
                </div>
              )}
            </div>
            
            {!isSystem && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit(role)}
                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Modifier
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onDelete(role.id, role.name)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  disabled={role.users_count > 0}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Supprimer
                </Button>
              </div>
            )}
            
            {isSystem && (
              <Badge variant="secondary" className="text-gray-600">
                Non modifiable
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Rôles Système */}
      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Lock className="h-5 w-5 text-blue-600" />
          Rôles Système ({systemRoles?.length})
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {systemRoles?.map((role) => (
            <RoleCard key={role.name} role={role} isSystem={true} />
          ))}
        </div>
      </div>

      {/* Rôles Personnalisés */}
      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Shield className="h-5 w-5 text-purple-600" />
          Rôles Personnalisés ({customRoles?.length})
        </h3>
        
        {customRoles?.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Shield className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 mb-2">Aucun rôle personnalisé créé</p>
              <p className="text-sm text-gray-500">
                Cliquez sur "Créer un Rôle" pour créer votre premier rôle personnalisé
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {customRoles?.map((role) => (
              <RoleCard key={role.id} role={role} isSystem={false} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RolesList;
